import { LitElement, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AudioFile } from '../config';
import './slider.js';

@customElement('audio-player')
export class AudioPlayer extends LitElement {
  @property({ type: Number }) chainNumber: 1 | 2 = 1;
  @property({ type: String }) label = 'Player';
  @property({ type: Array }) files: AudioFile[] = [];
  
  @state() private isPlaying = false;
  @state() private currentFile = '';
  @state() private currentTime = 0;
  @state() private duration = 0;
  @state() private selectedFile = '';
  @state() private currentIndex = 0;
  
  private audioElement: HTMLAudioElement | null = null;
  private timeUpdateInterval: number | null = null;


  protected createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  protected firstUpdated(): void {
    this.createAudioElement();
    this.startTimeUpdate();
    this.setupAudioServiceListener();
  }

  protected updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    
    // When files array is set/updated, randomize and select the first file
    if (changedProperties.has('files') && this.files.length > 0 && !this.selectedFile) {
      // Shuffle the files array using Fisher-Yates algorithm
      this.files = this.shuffleArray([...this.files]);
      console.log('Files loaded and shuffled, selecting first file:', this.files[0]);
      this.selectFileByIndex(0);
    }
  }
  
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopTimeUpdate();
    if (this.audioElement) {
      this.audioElement.remove();
    }
  }
  
  private createAudioElement(): void {
    this.audioElement = document.createElement('audio');
    this.audioElement.crossOrigin = 'anonymous';
    this.audioElement.preload = 'none'; // Don't preload for streaming
    this.audioElement.loop = false; // Don't loop individual tracks - we handle progression manually

    this.audioElement.addEventListener('loadedmetadata', () => {
      this.duration = this.audioElement?.duration || 0;
    });
    
    this.audioElement.addEventListener('play', () => {
      this.isPlaying = true;
    });
    
    this.audioElement.addEventListener('pause', () => {
      this.isPlaying = false;
    });
    
    this.audioElement.addEventListener('ended', () => {
      this.isPlaying = false;
      this.playNextTrack();
    });
    
    this.audioElement.addEventListener('timeupdate', () => {
      this.currentTime = this.audioElement?.currentTime || 0;
    });

    // Connect to Web Audio API for effects processing if available and initialized
    if (window.audioService && this.audioElement && window.audioService.isAudioContextInitialized()) {
      window.audioService.connectHTMLAudioElement(this.audioElement, this.chainNumber);
    }
    
    // Append to light DOM (since we disabled shadow DOM)
    this.appendChild(this.audioElement);
  }
  
  private startTimeUpdate(): void {
    this.timeUpdateInterval = window.setInterval(() => {
      if (this.audioElement && this.isPlaying) {
        this.currentTime = this.audioElement.currentTime;
      }
    }, 100);
  }
  
  private stopTimeUpdate(): void {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
  }

  private setupAudioServiceListener(): void {
    if (window.audioService) {
      window.audioService.addEventListener((event: string) => {
        if (event === 'initialized' && this.audioElement) {
          // Connect the audio element now that the AudioContext is initialized
          window.audioService.connectHTMLAudioElement(this.audioElement, this.chainNumber);
          console.log(`Audio player ${this.chainNumber} connected to Web Audio API`);
        }
      });
    }
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  
  private async selectFile(file: string): Promise<void> {
    const index = this.files.findIndex(f => f.path === file);
    if (index >= 0) {
      // Check if we should auto-play based on current playing state
      const shouldAutoPlay = this.isPlaying;
      await this.selectFileByIndex(index, shouldAutoPlay);
    }
  }

  private async selectFileByIndex(index: number, shouldAutoPlay: boolean = false): Promise<void> {
    if (index < 0 || index >= this.files.length) return;
    
    this.currentIndex = index;
    const file = this.files[index];
    console.log('Selected file:', file.path);
    this.selectedFile = file.path;
    this.currentFile = this.selectedFile;

    if (!this.audioElement) return;

    // For iOS compatibility, try to start playback immediately with user gesture
    if (shouldAutoPlay) {
      try {
        // Set source and immediately try to play to preserve user gesture
        this.audioElement.src = this.selectedFile;
        await this.audioElement.play();
        console.log('iOS-compatible playback started successfully');
      } catch (error) {
        console.log('Direct playback failed, trying load-then-play approach:', error);
        // If direct play fails, load first then try to play
        this.audioElement.load();
        
        // Listen for canplay event to try playing again
        const onCanPlay = async () => {
          this.audioElement?.removeEventListener('canplay', onCanPlay);
          try {
            if (this.audioElement) {
              await this.audioElement.play();
            }
          } catch (error) {
            console.error('Failed to auto-play after load:', error);
          }
        };
        
        this.audioElement.addEventListener('canplay', onCanPlay);
      }
    } else {
      // Just load without playing
      this.audioElement.src = this.selectedFile;
      this.audioElement.load();
    }
  }

  private async playNextTrack(): Promise<void> {
    const nextIndex = (this.currentIndex + 1) % this.files.length;
    this.selectFileByIndex(nextIndex, true); // Auto-play the next track
  }

  public async playPreviousTrack(): Promise<void> {
    const prevIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.files.length - 1;
    this.selectFileByIndex(prevIndex, this.isPlaying);
  }

  public async play(): Promise<void> {
    if (!this.audioElement) return;
    
    // Resume AudioContext if suspended (iOS fix)
    if (window.audioService && window.audioService.getAudioContextState && 
        window.audioService.getAudioContextState() === 'suspended') {
      try {
        await window.audioService.forceResumeAudioContext();
      } catch (error) {
        console.warn('Could not resume AudioContext:', error);
      }
    }
    
    try {
      if (this.isPlaying) {
        this.audioElement.pause();
      } else {
        await this.audioElement.play();
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  }

  private onProgressChange(e: CustomEvent): void {
    if (!this.audioElement || !this.duration) return;
    
    const newTime = e.detail.value;
    this.audioElement.currentTime = newTime;
    this.currentTime = newTime;
  }
  
  private formatTime(seconds: number): string {
    if (!isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  
  
  private get availableFiles() {
    return this.files;
  }

  render() {
    return html`
      <h1>${this.label}</h1>

      <div class="progress-container ${this.currentFile ? 'active' : ''}">
        <div class="slider-container">
          <div class="slider-row">
            <div class="slider-track">
              <app-slider
                .value=${this.currentTime}
                min="0"
                .max=${this.duration || 100}
                step="0.1"
                height="20"
                foregroundColor="#ffffff"
                backgroundColor="rgba(255, 255, 255, 0.1)"
                @input=${this.onProgressChange}
              ></app-slider>
            </div>
          </div>
          <div class="slider-text-row justify-between">
            <span class="text-left">${this.formatTime(this.currentTime)}</span>
            <span class="text-right">${this.formatTime(this.duration)}</span>
          </div>
        </div>
      </div>


      <ul>
        ${this.availableFiles.map((file: AudioFile) => html`
          <li
                 class="${file.path === this.currentFile ? 'active' : ''}"
                 @click="${() => this.selectFile(file.path)}">${file.title} <span class="artist">by ${file.artist}</span>
          </li>
        `)}
      </ul>
    `;
  }
}